import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Loading from "../components/Loading";
import { useGetTeen20ResultsQuery, useGetInplayBetsQuery } from "../services/casino/casinoApi";
import { getResolvedWsBaseUrl } from "../utils/domainConfig";

const GAME_CONFIG = {
  1: { name: "20-20 Dragon Tiger",  wsGame: "dt20",     slug: "dt20"     },
  2: { name: "Lucky 7 - B",         wsGame: "lucky7eu", slug: "lucky7eu" },
  3: { name: "Amar Akbar Anthony",  wsGame: "aaa",      slug: "aaa"      },
  4: { name: "Teenpatti 2020",      wsGame: "teen20",   slug: "teen20"   },
  5: { name: "Teen Patti One Day",  wsGame: "teen1",    slug: "teen1"    },
  6: { name: "Andar Bahar",         wsGame: "ab20",     slug: "ab20"     },
  7: { name: "Andar Bahar 2",       wsGame: "abj",      slug: "abj"      },
  8: { name: "32 Cards - A",        wsGame: "card32",   slug: "card32"   },
};

// ── Casino live TV — EK URL sabke liye: https://tv.sky99.co/casino/<game>
// (tv.sky99.co ka apna player: plain hls.js + auto-recovery, same-origin HLS — kisi domain pe CORS nahi)
const CASINO_TV_URL = "https://tv.sky99.co/casino";
const LiveVideo = ({ wsGame, title }) => (
  <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e5e5e5" }}>
    <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#000" }}>
      {wsGame && (
        <iframe
          src={`${CASINO_TV_URL}/${encodeURIComponent(wsGame)}`}
          title={title}
          allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
          allowFullScreen
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0, borderRadius: 12 }}
        />
      )}
    </div>
  </div>
);

const getCardImage = (cardCode) => {
  if (!cardCode || cardCode === "1")
    return "https://versionobj.ecoassetsservice.com/v14/static/front/img/cards/1.jpg";
  // Card code (HH/SS/DD/CC) image filename se seedha match karta hai — koi suit-swap NAHI (pehle HH→SS→DD→HH swap tha, jisse admin me TV se alag suit dikhte the).
  return `https://versionobj.ecoassetsservice.com/v14/static/front/img/cards/${String(cardCode).trim()}.jpg`;
};

const Card = ({ code, label }) => (
  <div className="flex flex-col items-center gap-1.5">
    {label && (
      <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#6b7280" }}>
        {label}
      </span>
    )}
    <img
      src={getCardImage(code)}
      alt="card"
      className="rounded object-cover shadow-lg"
      style={{ width: 36, height: 50, border: "2px solid rgba(255,255,255,0.15)" }}
    />
  </div>
);

// ── Cards sections ────────────────────────────────────────────
const DT20Cards = ({ wsData }) => (
  <div className="flex items-end justify-around py-2">
    <div className="flex flex-col items-center gap-2">
      <div className="text-xs font-bold tracking-widest uppercase" style={{ color: "#f87171" }}>Dragon</div>
      <Card code={wsData?.t1?.C1} />
    </div>
    <div className="flex flex-col items-center gap-1">
      <span className="text-xs" style={{ color: "#64748b" }}>VS</span>
    </div>
    <div className="flex flex-col items-center gap-2">
      <div className="text-xs font-bold tracking-widest uppercase" style={{ color: "#60a5fa" }}>Tiger</div>
      <Card code={wsData?.t1?.C2} />
    </div>
  </div>
);

const SingleCard = ({ wsData, label = "Card" }) => (
  <div className="flex justify-center py-2">
    <Card code={wsData?.t1?.C1} label={label} />
  </div>
);

const Teen20Cards = ({ wsData }) => {
  const c = (k) => wsData?.t1?.[k];
  return (
    <div className="flex items-end justify-between px-4 py-2">
      <div className="flex flex-col items-center gap-2">
        <span className="text-xs font-bold tracking-widest uppercase" style={{ color: "#a78bfa" }}>Player A</span>
        <div className="flex gap-2">
          <Card code={c("C1")} /><Card code={c("C2")} /><Card code={c("C3")} />
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <span className="text-xs font-bold tracking-widest uppercase" style={{ color: "#fb923c" }}>Player B</span>
        <div className="flex gap-2">
          <Card code={c("C4")} /><Card code={c("C5")} /><Card code={c("C6")} />
        </div>
      </div>
    </div>
  );
};

// Andar Bahar (ab20 / abj): C1 = joker, t1.cards = joker + baante gaye cards (khule nahi to "1")
const AndarBaharCards = ({ wsData }) => {
  const joker = wsData?.t1?.C1;
  const dealt = String(wsData?.t1?.cards || "").split(",").slice(1).filter((c) => c && c !== "1");
  return (
    <div className="flex items-center gap-4 px-4 py-2">
      <Card code={joker} label="Joker" />
      <div className="flex flex-col gap-1.5 min-w-0">
        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#6b7280" }}>Cards</span>
        <div className="flex gap-1.5 flex-wrap">
          {dealt.length ? dealt.slice(-14).map((c, i) => <Card key={i} code={c} />) : <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>}
        </div>
      </div>
    </div>
  );
};

// 32 Cards: t1.cards me 36 jagah — Player 8/9/10/11 ko baari-baari (i % 4), khule nahi to "1"
const Card32Cards = ({ wsData }) => {
  const all = String(wsData?.t1?.cards || "").split(",");
  const players = [8, 9, 10, 11].map((p, idx) => ({ p, cards: all.filter((c, i) => i % 4 === idx && c && c !== "1") }));
  return (
    <div className="grid grid-cols-4 gap-2 px-2 py-2">
      {players.map(({ p, cards }) => (
        <div key={p} className="flex flex-col items-center gap-1.5">
          <span className="text-xs font-bold tracking-widest uppercase" style={{ color: CARD32_COLORS[p] }}>Player {p}</span>
          <div className="flex gap-1 flex-wrap justify-center">
            {cards.length ? cards.map((c, i) => <Card key={i} code={c} />) : <Card code="1" />}
          </div>
        </div>
      ))}
    </div>
  );
};
const CARD32_COLORS = { 8: "#7c3aed", 9: "#ea580c", 10: "#2563eb", 11: "#16a34a" };

// ── Result history ────────────────────────────────────────────
const ResultHistory = ({ gameId, wsData, resultsData }) => {
  const getBubble = (r) => {
    const winner = r?.winner ?? r?.desc?.split("#")[0] ?? "";
    if (gameId === 1) {
      if (winner === "Dragon") return { lbl: "D", bg: "#ef4444", shadow: "0 0 8px #ef4444aa" };
      if (winner === "Tiger")  return { lbl: "T", bg: "#3b82f6", shadow: "0 0 8px #3b82f6aa" };
      return                          { lbl: "T", bg: "#10b981", shadow: "0 0 8px #10b981aa" };
    }
    if (gameId === 2) {
      if (winner === "High" || winner === "High Card" || winner === "HIGH Card") return { lbl: "H", bg: "#d97706", shadow: "0 0 8px #d97706aa" };
      if (winner === "Low"  || winner === "Low Card"  || winner === "LOW Card")  return { lbl: "L", bg: "#475569", shadow: "0 0 8px #47556988" };
      return { lbl: "T", bg: "#10b981", shadow: "0 0 8px #10b981aa" };
    }
    if (gameId === 3) {
      if (winner === "Amar")    return { lbl: "A", bg: "#dc2626", shadow: "0 0 8px #dc2626aa" };
      if (winner === "Akbar")   return { lbl: "B", bg: "#2563eb", shadow: "0 0 8px #2563ebaa" };
      if (winner === "Anthony") return { lbl: "C", bg: "#16a34a", shadow: "0 0 8px #16a34aaa" };
      return { lbl: winner?.charAt(0) ?? "?", bg: "#64748b", shadow: "none" };
    }
    if (gameId === 6 || gameId === 7) {
      if (/^andar/i.test(winner)) return { lbl: "A", bg: "#2563eb", shadow: "0 0 8px #2563ebaa" };
      if (/^bahar/i.test(winner)) return { lbl: "B", bg: "#dc2626", shadow: "0 0 8px #dc2626aa" };
    }
    if (gameId === 8) {
      const n = Number((winner.match(/\d+/) || [])[0]);
      if (CARD32_COLORS[n]) return { lbl: String(n), bg: CARD32_COLORS[n], shadow: `0 0 8px ${CARD32_COLORS[n]}aa` };
    }
    return { lbl: "?", bg: "#64748b", shadow: "none" };
  };

  let items = [];
  if (gameId === 4 || gameId === 5) {
    // Try WS t3 first, fallback to API
    const t3 = wsData?.t3 ?? [];
    if (t3.length > 0) {
      items = t3.slice(0, 12).map((r) => {
        const w = r?.winner ?? "";
        const isA = w === "Player A" || w === "A" || r?.result === "1";
        return { lbl: isA ? "A" : "B", bg: isA ? "#7c3aed" : "#ea580c", shadow: isA ? "0 0 8px #7c3aed88" : "0 0 8px #ea580c88" };
      });
    } else {
      const apiList =
        resultsData?.data ??
        resultsData?.result?.data ??
        resultsData?.result ??
        [];
      items = (Array.isArray(apiList) ? apiList : []).slice(0, 20).map((item) => {
        const w = item?.winner ?? "";
        const isA = w === "Player A" || item?.result === "1";
        return { lbl: isA ? "A" : "B", bg: isA ? "#7c3aed" : "#ea580c", shadow: isA ? "0 0 8px #7c3aed88" : "0 0 8px #ea580c88" };
      });
    }
  } else {
    items = (wsData?.t3 ?? []).slice(0, 12).map(getBubble);
  }

  if (items.length === 0)
    return <span style={{ color: "#9ca3af", fontSize: 12 }}>No history yet</span>;

  return (
    <div className="flex gap-2 flex-wrap">
      {items.map((item, i) => (
        <div
          key={i}
          title={item.lbl}
          style={{
            width: 32, height: 32,
            borderRadius: "50%",
            background: item.bg,
            boxShadow: item.shadow,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 700, color: "#fff",
            cursor: "default",
          }}
        >
          {item.lbl}
        </div>
      ))}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────
const CasinoGameDetailPage = () => {
  const { gameId: gameIdParam } = useParams();
  const navigate = useNavigate();
  const gameId = parseInt(gameIdParam) || 4;
  const config = GAME_CONFIG[gameId] || GAME_CONFIG[4];

  const [wsData, setWsData]           = useState(null);
  const [countdown, setCountdown]     = useState("--");
  const [isConnected, setIsConnected] = useState(false);
  const [roundId, setRoundId]         = useState("");
  const timerRef = useRef(null);

  // WebSocket
  useEffect(() => {
    const ws = new WebSocket(`${getResolvedWsBaseUrl(import.meta.env.VITE_WS_URL)}/ws/casino`);
    ws.onopen  = () => { setIsConnected(true); ws.send(JSON.stringify({ type: "subscribe", game: config.wsGame })); };
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "gameData" && msg.game === config.wsGame) {
          setWsData(msg);
          if (msg.roundId || msg.t1?.mid) setRoundId(msg.roundId ?? msg.t1?.mid);
        }
      } catch {}
    };
    ws.onerror = () => setIsConnected(false);
    ws.onclose = () => setIsConnected(false);
    return () => { if (ws.readyState === WebSocket.OPEN) ws.close(); };
  }, [config.wsGame]);

  // Countdown
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const autotime = wsData?.autotime ?? wsData?.t1?.autotime;
    if (!autotime) { setCountdown("00:00"); return; }
    const secs = parseInt(autotime);
    if (secs <= 0) { setCountdown("00:00"); return; }
    const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
    let rem = secs;
    setCountdown(fmt(rem));
    timerRef.current = setInterval(() => {
      rem -= 1;
      if (rem <= 0) { setCountdown("00:00"); clearInterval(timerRef.current); }
      else setCountdown(fmt(rem));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [wsData?.autotime, wsData?.t1?.autotime]);

  const { data: resultsData } = useGetTeen20ResultsQuery(undefined, { skip: gameId !== 4, pollingInterval: 5000 });
  const { data: betsData, isLoading: betsLoading } = useGetInplayBetsQuery(config.slug, { pollingInterval: 2000 });

  const liveBets = betsData?.data || [];
  const displayRoundId = roundId || wsData?.t1?.mid || wsData?.roundId || "—";
  const isClosed = countdown === "00:00";

  return (
    <div style={{ background: "#f5f5f7", minHeight: "100vh", padding: "16px" }}>
      <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">

        {/* ── Left panel ── */}
        <div className="lg:col-span-4 flex flex-col gap-3">

          {/* Header */}
          <div
            style={{
              background: "var(--chrome-bg)",
              border: "1px solid var(--primary-2)",
              borderRadius: 12,
              padding: "14px 20px",
            }}
            className="flex items-center justify-between gap-2 flex-wrap"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <span style={{ color: "var(--chrome-text)", fontSize: 18, fontWeight: 600 }}>{config.name}</span>
              <span style={{ color: "var(--chrome-text-3)", fontSize: 11 }}>Round: {displayRoundId}</span>

              {/* Timer */}
              <div
                style={{
                  background: isClosed ? "#fee2e2" : "#dcfce7",
                  border: `1px solid ${isClosed ? "#dc2626" : "#16a34a"}`,
                  color: isClosed ? "#dc2626" : "#16a34a",
                  borderRadius: 20,
                  padding: "2px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: 1,
                }}
              >
                {isClosed ? "CLOSED" : countdown}
              </div>

              {/* Live indicator */}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span
                  style={{
                    width: 7, height: 7, borderRadius: "50%",
                    background: isConnected ? "#16a34a" : "#9ca3af",
                    boxShadow: isConnected ? "0 0 6px #16a34a" : "none",
                    display: "inline-block",
                  }}
                />
                <span style={{ color: isConnected ? "#16a34a" : "#3a3a2a", fontSize: 11, fontWeight: 600 }}>
                  {isConnected ? "LIVE" : "Connecting"}
                </span>
              </div>
            </div>

            <button
              onClick={() => navigate(-1)}
              style={{
                background: "rgba(0,0,0,0.06)",
                border: "1px solid var(--primary-2)",
                color: "var(--chrome-text)",
                padding: "6px 16px",
                borderRadius: 8,
                fontSize: 13,
                cursor: "pointer",
              }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = "#1a1a2e"; e.currentTarget.style.background = "rgba(0,0,0,0.12)"; }}
              onMouseOut={(e)  => { e.currentTarget.style.borderColor = "var(--primary-2)"; e.currentTarget.style.background = "rgba(0,0,0,0.06)"; }}
            >
              ← Back
            </button>
          </div>

          {/* Video stream (HLS live) */}
          <LiveVideo wsGame={config.wsGame} title={config.name} />

          {/* Cards */}
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e5e5",
              borderRadius: 12,
              padding: "12px 16px",
            }}
          >
            {gameId === 1 && <DT20Cards wsData={wsData} />}
            {gameId === 2 && <SingleCard wsData={wsData} label="Card" />}
            {gameId === 3 && <SingleCard wsData={wsData} label="Card" />}
            {gameId === 4 && <Teen20Cards wsData={wsData} />}
            {gameId === 5 && <Teen20Cards wsData={wsData} />}
            {(gameId === 6 || gameId === 7) && <AndarBaharCards wsData={wsData} />}
            {gameId === 8 && <Card32Cards wsData={wsData} />}
          </div>

          {/* Result history */}
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e5e5",
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: "#9ca3af", marginBottom: 10, textTransform: "uppercase" }}>
              Last Results
            </div>
            <ResultHistory gameId={gameId} wsData={wsData} resultsData={resultsData} />
          </div>

        </div>

        {/* ── Right panel: bets ── */}
        <div className="lg:col-span-3">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e5e5",
              borderRadius: 12,
              overflow: "hidden",
              position: "sticky",
              top: 16,
            }}
          >
            {/* Panel header */}
            <div
              style={{
                background: "var(--chrome-bg)",
                borderBottom: "1px solid var(--primary-2)",
                padding: "14px 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ color: "var(--chrome-text)", fontWeight: 600, fontSize: 15 }}>Casino Bets</span>
              <span
                style={{
                  background: "#dcfce7",
                  border: "1px solid #16a34a",
                  color: "#16a34a",
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 20,
                  letterSpacing: 1,
                }}
              >
                {liveBets.length} LIVE
              </span>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto", maxHeight: 580, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f7f7f8", position: "sticky", top: 0, zIndex: 1 }}>
                    {["Player", "Runner", "Odds", "Amount", "Time"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "10px 12px",
                          textAlign: "left",
                          color: "#6b7280",
                          fontWeight: 600,
                          fontSize: 11,
                          letterSpacing: 1,
                          textTransform: "uppercase",
                          borderBottom: "1px solid #e5e5e5",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {betsLoading ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 40, textAlign: "center" }}>
                        <Loading />
                      </td>
                    </tr>
                  ) : liveBets.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 48, textAlign: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, color: "#d1d5db" }}>
                          <svg width="40" height="40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span style={{ color: "#9ca3af", fontSize: 13 }}>No bets placed yet</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    liveBets.map((bet, i) => (
                      <tr
                        key={i}
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          background: i % 2 === 0 ? "transparent" : "#fafafa",
                          transition: "background 0.15s",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = "rgba(244,192,66,0.15)")}
                        onMouseOut={(e)  => (e.currentTarget.style.background = i % 2 === 0 ? "transparent" : "#fafafa")}
                      >
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ color: "#1a1a2e", fontWeight: 600, lineHeight: 1.3 }}>
                            {bet.user?.fullName || bet.user?.fullname || bet.fullName || "—"}
                          </div>
                          <div style={{ color: "#6b7280", fontSize: 11 }}>
                            {bet.user?.username || bet.username || ""}
                          </div>
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <span
                            style={{
                              background: "#f7f7f8",
                              border: "1px solid #e5e5e5",
                              borderRadius: 6,
                              padding: "2px 8px",
                              color: "#6b7280",
                              fontSize: 11,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {bet.betOn || "—"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", color: "#059669", fontWeight: 600 }}>
                          {bet.odds || "—"}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#1a1a2e", fontWeight: 500 }}>
                          {bet.stake ? `₹${Number(bet.stake).toLocaleString("en-IN")}` : "—"}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#6b7280", fontSize: 11, whiteSpace: "nowrap" }}>
                          {bet.placedAt
                            ? new Date(bet.placedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
                            : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CasinoGameDetailPage;
